import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 1.5rem 2.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 50% 0%, rgba(0, 122, 255, 0.15), transparent 70%);
  }
`;

const LoginCard = styled.div`
  position: relative;
  z-index: 1;
  background: rgba(14, 18, 28, 0.65);
  border-radius: 32px;
  width: 100%;
  max-width: 460px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: saturate(180%) blur(40px);
  -webkit-backdrop-filter: saturate(180%) blur(40px);
  box-shadow: 
    0 40px 100px -20px rgba(0, 0, 0, 0.7),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
`;

const LoginHeader = styled.div`
  padding: 3.5rem 2.5rem 2rem;
  text-align: center;
  color: #fff;

  h1 {
    margin: 0 0 12px 0;
    font-size: clamp(2.2rem, 5vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.65);
    font-size: 1.05rem;
    font-weight: 400;
  }
`;

const LoginForm = styled.form`
  padding: 1rem 2.5rem 3.5rem;
  color: #f5f8ff;
`;

const FormField = styled.div`
  margin-bottom: 24px;
  
  label {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    padding-left: 4px;
  }
  
  input {
    width: 100%;
    padding: 16px 20px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 18px;
    font-size: 1rem;
    transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
    background: rgba(255, 255, 255, 0.03);
    color: #fff;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.25);
    }
    
    &:focus {
      outline: none;
      border-color: rgba(90, 200, 255, 0.5);
      background: rgba(255, 255, 255, 0.06);
      box-shadow: 
        0 0 0 4px rgba(90, 200, 255, 0.1),
        inset 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    &.error {
      border-color: #ff453a;
      background: rgba(255, 69, 58, 0.05);
      
      &:focus {
        box-shadow: 0 0 0 4px rgba(255, 69, 58, 0.1);
      }
    }
  }
  
  .error-message {
    color: #ff453a;
    font-size: 0.85rem;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 4px;
    font-weight: 500;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  background: #007aff;
  color: white;
  border: none;
  padding: 18px;
  border-radius: 20px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  margin-top: 10px;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px -5px rgba(0, 122, 255, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    background: #0062cc;
    box-shadow: 0 20px 40px -10px rgba(0, 122, 255, 0.5);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DividerText = styled.div`
  text-align: center;
  margin: 24px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
  
  span {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }
`;

const RegisterLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 600;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.02);
  font-size: 1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const ForgotLink = styled(Link)`
  display: inline-block;
  margin-top: 12px;
  font-size: 0.9rem;
  color: #007aff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
  padding-left: 4px;

  &:hover {
    color: #66b2ff;
    text-decoration: none;
  }
`;

const DemoAccounts = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 1.5rem;
  margin-top: 2rem;
  border-radius: 24px;
  
  h4 {
    margin: 0 0 16px 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .demo-account {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    
    &:last-child {
      border-bottom: none;
    }
    
    .account-info {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.5);
      
      .email {
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 2px;
      }
    }
    
    .use-btn {
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.3);
      color: #007aff;
      padding: 6px 14px;
      border-radius: 999px;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #007aff;
        color: white;
        border-color: #007aff;
      }
    }
  }
`;

const AlertMessage = styled.div`
  padding: 16px;
  border-radius: 18px;
  margin-bottom: 24px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.4;
  
  &.success {
    background: rgba(48, 209, 88, 0.15);
    color: #30d158;
    border: 1px solid rgba(48, 209, 88, 0.2);
  }
  
  &.error {
    background: rgba(255, 69, 58, 0.15);
    color: #ff453a;
    border: 1px solid rgba(255, 69, 58, 0.2);
  }
`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const { login, isAuthenticated, user } = useAuthStore();

  // Check for session expiration message
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('expired') === 'true') {
      setError('Your session has expired. Please log in again to continue.');
    }
  }, [location.search]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'ADMIN') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'PLANNER') {
        navigate('/planner', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (field, value) => {
    if (field === 'email') {
      setEmail(value);
    } else if (field === 'password') {
      setPassword(value);
    }

    // Clear error when user starts typing
    if (error && error.includes(field)) {
      setError('');
    }

    // Clear messages
    if (success || error) {
      setSuccess('');
      setError('');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (Object.keys(errors).length > 0) {
      setError(Object.values(errors).join('\n'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await login({ email, password });

      if (result.success) {
        setSuccess('Login successful! Redirecting...');

        // Get user from result or localStorage
        const userData = JSON.parse(localStorage.getItem('user'));

        // Role-based redirect
        setTimeout(() => {
          if (userData?.role === 'ADMIN') {
            navigate('/admin');
          } else if (userData?.role === 'PLANNER') {
            navigate('/planner');
          } else {
            navigate(from, { replace: true });
          }
        }, 1000);
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoAccount = (email, password) => {
    setEmail(email);
    setPassword(password);
    setError('');
    setSuccess('');
  };

  return (
    <LoginContainer>
      <Helmet>
        <title>Sign In - TikPlat</title>
      </Helmet>

      <LoginCard>
        <LoginHeader>
          <h1>Welcome Back</h1>
          <p>Sign in to your TikPlat account</p>
        </LoginHeader>

        <LoginForm onSubmit={handleSubmit}>
          {success && (
            <AlertMessage className="success">
              <span role="img" aria-label="success">✅</span>
              {success}
            </AlertMessage>
          )}
          {error && (
            <AlertMessage className="error">
              <span role="img" aria-label="error">⚠️</span>
              {error}
            </AlertMessage>
          )}

          <FormField>
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={error && error.toLowerCase().includes('email') ? 'error' : ''}
              placeholder="Enter your email"
              autoComplete="email"
            />
          </FormField>

          <FormField>
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={error && error.toLowerCase().includes('password') ? 'error' : ''}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <ForgotLink to="/reset-password">Forgot password?</ForgotLink>
          </FormField>

          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </LoginButton>

          <DividerText>
            <span>Or continue with demo</span>
          </DividerText>

          <DemoAccounts>
            <h4>✨ Try Demo Accounts</h4>
            <div className="demo-account">
              <div className="account-info">
                <div className="email">admin@tikplat.com</div>
                <div>👑 Administrator</div>
              </div>
              <button
                type="button"
                className="use-btn"
                onClick={() => fillDemoAccount('admin@tikplat.com', 'Admin123')}
              >
                Use
              </button>
            </div>

            <div className="demo-account">
              <div className="account-info">
                <div className="email">planner@tikplat.com</div>
                <div>🎭 Event Planner</div>
              </div>
              <button
                type="button"
                className="use-btn"
                onClick={() => fillDemoAccount('planner@tikplat.com', 'Planner123')}
              >
                Use
              </button>
            </div>

            <div className="demo-account">
              <div className="account-info">
                <div className="email">promoter@tikplat.com</div>
                <div>🎪 Event Promoter</div>
              </div>
              <button
                type="button"
                className="use-btn"
                onClick={() => fillDemoAccount('promoter@tikplat.com', 'Promoter123')}
              >
                Use
              </button>
            </div>

            <div className="demo-account">
              <div className="account-info">
                <div className="email">staff@tikplat.com</div>
                <div>👥 Staff Member</div>
              </div>
              <button
                type="button"
                className="use-btn"
                onClick={() => fillDemoAccount('staff@tikplat.com', 'Staff123')}
              >
                Use
              </button>
            </div>

            <div className="demo-account">
              <div className="account-info">
                <div className="email">buyer@tikplat.com</div>
                <div>🛒 Buyer</div>
              </div>
              <button
                type="button"
                className="use-btn"
                onClick={() => fillDemoAccount('buyer@tikplat.com', 'Buyer123')}
              >
                Use
              </button>
            </div>

            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
              <span role="img" aria-label="bulb" style={{ marginRight: '6px' }}>💡</span>
              New here? Click "Create Account" below.
            </div>
          </DemoAccounts>

          <div style={{ height: '32px' }}></div>

          <RegisterLink to="/register">
            Create New Account
          </RegisterLink>
        </LoginForm>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;
